import type { BaseReactProps, ReactNodeDef } from '@shared/node-v1.0.0'
import { getPortDef, sizes } from '@shared/port-v1.0.0'

export type Props = BaseReactProps & {
	defaultValue?: string | number
	clamp: boolean
	allowDecimal: boolean
	decimalScale?: number
}

import Comp from '../component'

export default {
	hashTag: '#pre-release',
	module: { static: Comp },
	inNode: {
		inputs: [
			getPortDef({ name: 'label', displayName: 'Label', group: 'Params', type: 'string' }),
			getPortDef({ name: 'placeholder', displayName: 'Placeholder', group: 'Params', type: 'string' }),
			getPortDef({ name: 'description', displayName: 'Description', group: 'Params', type: 'string' }),
			getPortDef({ name: 'min', displayName: 'Min', group: 'Params', type: 'number' }),
			getPortDef({ name: 'max', displayName: 'Max', group: 'Params', type: 'number' }),
			getPortDef({ name: 'step', displayName: 'Step', group: 'Params', type: 'number' }),
			getPortDef({ name: 'defaultValue', displayName: 'Default value', group: 'Data', type: 'number' }),
			getPortDef({ name: 'w', displayName: 'Width', group: 'Dimensions', type: 'string' }),
			getPortDef({ name: 'size', displayName: 'Size', group: 'Dimensions', type: sizes, default: 'sm' }),
			getPortDef({ name: 'disabled', displayName: 'Disabled', group: 'States', type: 'boolean' }),
			getPortDef({ name: 'error', displayName: 'Error', group: 'States', type: '*' }),
			getPortDef({
				name: 'variant',
				displayName: 'Variant',
				group: 'Styles',
				type: [
					{ value: 'default', label: 'Default' },
					{ value: 'filled', label: 'Filled' },
					{ value: 'unstyled', label: 'Unstyled' },
				],
				default: 'default',
			}),
			getPortDef({
				name: 'radius',
				displayName: 'Radius',
				group: 'Styles',
				type: [{ value: '0', label: 'none' }, ...sizes],
				default: 'md',
			}),
			getPortDef({ name: 'withAsterisk', displayName: 'With asterisk', group: 'Styles', type: 'boolean', default: false }),
			getPortDef({
				name: 'hideControls',
				displayName: 'Hide controls',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'boolean',
				default: false,
			}),
			getPortDef({
				name: 'clamp',
				displayName: 'Clamp',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'boolean',
				default: false,
			}),
			getPortDef({ name: 'prefix', displayName: 'Prefix', group: 'Custom', customGroup: 'Additional params', type: 'string' }),
			getPortDef({ name: 'suffix', displayName: 'Suffix', group: 'Custom', customGroup: 'Additional params', type: 'string' }),
			getPortDef({
				name: 'allowNegative',
				displayName: 'Allow negative',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'boolean',
				default: true,
			}),
			getPortDef({
				name: 'allowDecimal',
				displayName: 'Allow decimal',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'boolean',
				default: true,
			}),
			getPortDef({
				name: 'decimalScale',
				displayName: 'Decimal scale',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'number',
				dependsOn: (p: Props) => p.allowDecimal,
			}),
			getPortDef({
				name: 'fixedDecimalScale',
				displayName: 'Fixed decimal scale',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'boolean',
				default: false,
				dependsOn: (p: Props) => Boolean(p.allowDecimal && p.decimalScale),
			}),
			getPortDef({
				name: 'thousandSeparator',
				displayName: 'Thousand separator',
				group: 'Custom',
				customGroup: 'Additional params',
				type: 'string',
				default: ' ',
			}),
			getPortDef({ name: 'increment', displayName: 'Increment', group: 'Signals', type: 'signal' }),
			getPortDef({ name: 'decrement', displayName: 'Decrement', group: 'Signals', type: 'signal' }),
			getPortDef({ name: 'reset', displayName: 'Reset', group: 'Signals', type: 'signal' }),
			getPortDef({ name: 'stepHoldDelay', displayName: 'Step hold delay', group: 'Advanced', type: 'number', default: 500 }),
			getPortDef({
				name: 'stepHoldInterval',
				displayName: 'Step hold interval',
				group: 'Advanced',
				type: 'funcEval',
				codeComment: '//(t) => { return Math.max(1000 / t ** 2, 25) }',
			}),
		],
		outputs: [
			getPortDef({ name: 'value', displayName: 'Value', group: 'Data', type: 'number' }),
			getPortDef({ name: 'changed', displayName: 'Changed', group: 'Signals', type: 'signal' }),
			getPortDef({ name: 'reseted', displayName: 'Reseted', group: 'Signals', type: 'signal' }),
		],
	},
} satisfies ReactNodeDef
